import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader2 } from 'lucide-react';
import api from '../../../api.js';
import { useFormat } from '../../../i18n/format.js';
import { useToast } from '../../common/Toast.jsx';

/**
 * ContractForm — création/édition d'un contrat d'assurance.
 *
 * Props :
 * - contract : objet existant (edit) ou null (create)
 * - onClose, onSuccess : callbacks standards
 * - lockedContractType : si présent ('motor_fleet' | 'motor_single'), verrouille
 *   le type de contrat dès l'ouverture ET filtre les produits disponibles pour
 *   n'afficher que ceux du bon type. Utilisé quand ContractForm est appelé en
 *   modal-over-modal depuis le wizard véhicule (Step 5) — l'utilisateur a déjà
 *   choisi Flotte/Individuel dans le Panel 1, pas question qu'il crée par
 *   inadvertance un contrat flotte alors qu'il visait un mono-véhicule.
 */
function ContractForm({ contract, onClose, onSuccess, lockedContractType = null }) {
  const { t, fmt } = useFormat();
  const toast = useToast();

  // Catalog data
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  // Selected company ID for filtering products
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const [formData, setFormData] = useState(contract || {
    insurer_name: '',
    policy_number: '',
    // Si le parent nous impose un type (wizard véhicule), on part déjà dessus.
    contract_type: lockedContractType || 'motor_fleet',
    product_name: '',
    status: 'draft',
    effective_date: '',
    expiry_date: '',
    payment_frequency: 'annual',
    total_premium_ttc: 0,
    deductible: 0,
    notes: ''
  });

  // Load catalog data on mount
  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const [companiesRes, productsRes, suppliersRes] = await Promise.all([
          api.getAll('insurance_company'),
          api.getAll('insurance_product_template'),
          api.supplier.getAll()
        ]);

        const companiesData = companiesRes?.data || companiesRes || [];
        const productsData = productsRes?.data || productsRes || [];
        const suppliersData = suppliersRes?.data || suppliersRes || [];

        const activeCompanies = companiesData
          .filter(c => c.is_active !== 0)
          .sort((a, b) => (a.short_name || a.name || '').localeCompare(b.short_name || b.name || '', 'fr', { sensitivity: 'base' }));
        setCompanies(activeCompanies);
        setProducts(productsData.filter(p => p.is_active !== 0));
        setBrokers(suppliersData.filter(s => ['broker', 'insurer', 'agent'].includes(s.category)));

        // If editing, find the company by name and set selectedCompanyId
        if (contract?.insurer_name) {
          const matchingCompany = companiesData.find(c =>
            c.name === contract.insurer_name ||
            c.short_name === contract.insurer_name
          );
          if (matchingCompany) {
            setSelectedCompanyId(matchingCompany.id);
          }
        }
      } catch (error) {
        console.error('Error loading catalog:', error);
      } finally {
        setLoadingCatalog(false);
      }
    };

    loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter products by selected company AND, if we were locked to a contract
  // type by the caller (wizard véhicule), by matching product.product_type
  // so the user can't pick a fleet product when they came in to create a
  // mono-véhicule contract.
  const filteredProducts = useMemo(() => {
    if (!selectedCompanyId) return [];
    const byCompany = products.filter(p => p.company_id === selectedCompanyId);
    if (!lockedContractType) return byCompany;
    const wantedType = lockedContractType === 'motor_single' ? 'single'
                     : lockedContractType === 'motor_fleet' ? 'fleet'
                     : null;
    if (!wantedType) return byCompany;
    return byCompany.filter(p => p.product_type === wantedType);
  }, [products, selectedCompanyId, lockedContractType]);

  // Handle company selection
  const handleCompanyChange = (companyId) => {
    const company = companies.find(c => c.id === parseInt(companyId));
    setSelectedCompanyId(company ? company.id : null);
    setFormData({
      ...formData,
      insurer_name: company ? (company.short_name || company.name) : '',
      product_name: '' // Reset product when company changes
    });
  };

  // Handle product selection — derives contract_type from product.product_type,
  // sauf si le parent a verrouillé le type (wizard véhicule), auquel cas on
  // garde le verrou — le filtre `filteredProducts` a déjà écarté les produits
  // du mauvais type, donc en théorie on n'arrive jamais ici avec un mismatch.
  const handleProductChange = (productName) => {
    const product = products.find(p => p.name === productName);
    const derivedType = lockedContractType
                      ? lockedContractType
                      : (product?.product_type === 'single' ? 'motor_single'
                         : product?.product_type === 'fleet'  ? 'motor_fleet'
                         : formData.contract_type);
    setFormData({ ...formData, product_name: productName, contract_type: derivedType });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Strip internal flags before sending
      const { _expiryAutoSet, ...payload } = formData;
      let savedContract = null;
      if (contract?.id) {
        const res = await api.update('insurance_contract', contract.id, payload);
        savedContract = { ...payload, id: contract.id, ...(res?.data || res || {}) };
        toast.success(t('insuranceConfig.contractForm.updatedSuccess'), t('insuranceConfig.toast.successTitle'));
      } else {
        const res = await api.create('insurance_contract', payload);
        const newId = res?.data?.id ?? res?.id;
        savedContract = { ...payload, id: newId, ...(res?.data || res || {}) };
        toast.success(t('insuranceConfig.contractForm.createdSuccess'), t('insuranceConfig.toast.successTitle'));
      }
      // Pass the saved contract to the callback so callers that need to auto-select
      // the newly-created record (e.g. VehicleLifecycleWorkflow Step 5) can do so
      // without a second round-trip. Existing callers that ignore the arg keep working.
      onSuccess(savedContract);
    } catch (error) {
      toast.error(error.message || t('insuranceConfig.toast.saveFailed'), t('insuranceConfig.toast.errorTitle'));
    }
  };

  if (loadingCatalog) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8 flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="text-gray-600">{t('insuranceConfig.contractForm.loadingCatalog')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex justify-between items-center">
          <h3 className="text-2xl font-bold text-white">
            {contract ? t('insuranceConfig.contractForm.titleEdit') : t('insuranceConfig.contractForm.titleCreate')}
          </h3>
          <button onClick={onClose} className="text-white hover:bg-blue-700 p-2 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>

        {lockedContractType && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-900 flex items-start gap-2">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-bold flex-shrink-0 mt-0.5">🔒</span>
            <div>
              <p className="font-semibold">
                {t('insuranceConfig.contractForm.lockedTitle', {
                  type: lockedContractType === 'motor_single'
                    ? t('insuranceConfig.productTypes.single')
                    : t('insuranceConfig.productTypes.fleet'),
                })}
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                {t('insuranceConfig.contractForm.lockedHint')}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.contractForm.insurer')}</label>
              <select
                required
                value={selectedCompanyId || ''}
                onChange={(e) => handleCompanyChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('insuranceConfig.contractForm.insurerPlaceholder')}</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.short_name || company.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.contractForm.policyNumber')}</label>
              <input
                type="text"
                required
                value={formData.policy_number}
                onChange={(e) => setFormData({...formData, policy_number: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={t('insuranceConfig.contractForm.policyNumberPlaceholder')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.contractForm.product')}</label>
              <select
                required
                value={formData.product_name}
                onChange={(e) => handleProductChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                disabled={!selectedCompanyId}
              >
                <option value="">
                  {selectedCompanyId
                    ? (filteredProducts.length > 0
                        ? t('insuranceConfig.contractForm.productPlaceholder')
                        : t('insuranceConfig.contractForm.productNoneAvailable'))
                    : t('insuranceConfig.contractForm.productPickInsurerFirst')
                  }
                </option>
                {filteredProducts.map(product => (
                  <option key={product.id} value={product.name}>
                    {product.name}
                  </option>
                ))}
              </select>
              {selectedCompanyId && filteredProducts.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  {t('insuranceConfig.contractForm.productNoneConfigured')}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.contractForm.contractType')}</label>
              <select
                value={formData.contract_type}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
              >
                <option value="motor_fleet">{t('insuranceConfig.contractForm.typeFleet')}</option>
                <option value="motor_single">{t('insuranceConfig.contractForm.typeSingle')}</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {lockedContractType
                  ? t('insuranceConfig.contractForm.typeLockedHint')
                  : t('insuranceConfig.contractForm.typeAutoHint')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.contractForm.effectiveDate')}</label>
              <input
                type="date"
                required
                value={formData.effective_date}
                onChange={(e) => {
                  const effectiveDate = e.target.value;
                  const updates = { effective_date: effectiveDate };
                  // Auto-set expiry to effective + 1 year if expiry is empty or was auto-set
                  if (effectiveDate && (!formData.expiry_date || formData._expiryAutoSet)) {
                    const d = new Date(effectiveDate);
                    d.setFullYear(d.getFullYear() + 1);
                    updates.expiry_date = d.toISOString().split('T')[0];
                    updates._expiryAutoSet = true;
                  }
                  setFormData({...formData, ...updates});
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.contractForm.expiryDate')}</label>
              <input
                type="date"
                required
                value={formData.expiry_date}
                onChange={(e) => setFormData({...formData, expiry_date: e.target.value, _expiryAutoSet: false})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {formData.expiry_date && (() => {
                const expiry = new Date(formData.expiry_date);
                const cancelDeadline = new Date(expiry);
                cancelDeadline.setMonth(cancelDeadline.getMonth() - 2);
                const negoStart = new Date(expiry);
                negoStart.setMonth(negoStart.getMonth() - 3);
                const today = new Date();
                const isNearCancel = today >= cancelDeadline;
                const isInNegoWindow = today >= negoStart && today < cancelDeadline;
                return (
                  <div className="mt-1 space-y-0.5">
                    <p className={`text-xs ${isNearCancel ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                      {t('insuranceConfig.contractForm.cancelBefore', { date: fmt.date(cancelDeadline) })}
                    </p>
                    <p className={`text-xs ${isInNegoWindow ? 'text-amber-600 font-semibold' : 'text-gray-500'}`}>
                      {t('insuranceConfig.contractForm.negotiationFrom', { date: fmt.date(negoStart) })}
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className={contract ? 'grid grid-cols-3 gap-4' : ''}>
            {contract && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.contractForm.premiumTtc')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.total_premium_ttc}
                    onChange={(e) => setFormData({...formData, total_premium_ttc: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.contractForm.deductible')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.deductible}
                    onChange={(e) => setFormData({...formData, deductible: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.contractForm.paymentFrequency')}</label>
              <select
                value={formData.payment_frequency}
                onChange={(e) => setFormData({...formData, payment_frequency: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="monthly">{t('insuranceConfig.contractForm.frequencyMonthly')}</option>
                <option value="quarterly">{t('insuranceConfig.contractForm.frequencyQuarterly')}</option>
                <option value="semi_annual">{t('insuranceConfig.contractForm.frequencySemiAnnual')}</option>
                <option value="annual">{t('insuranceConfig.contractForm.frequencyAnnual')}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.contractForm.intermediary')}</label>
            <select
              value={formData.intermediary_id || ''}
              onChange={(e) => setFormData({...formData, intermediary_id: e.target.value ? parseInt(e.target.value) : null})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('insuranceConfig.contractForm.intermediaryNone')}</option>
              {brokers.map(broker => (
                <option key={broker.id} value={broker.id}>
                  {broker.name} ({t(`insuranceConfig.intermediaryTypes.${broker.category}`, { defaultValue: broker.category })})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.contractForm.notes')}</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={t('insuranceConfig.contractForm.notesPlaceholder')}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {contract ? t('common.actions.edit') : t('common.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContractForm;
